import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, User, Mail, Phone, Shield, Sun, Moon, Smartphone, CheckCircle, XCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // 2FA state
  const [setupData, setSetupData] = useState(null); // { secret, qr_code }
  const [verifyCode, setVerifyCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) return toast.error('Enter your current password');
    if (!newPassword) return toast.error('Enter a new password');
    if (newPassword.length < 6) return toast.error('New password must be at least 6 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');

    setSaving(true);
    try {
      const res = await api.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(res.data.status || 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    }
    setSaving(false);
  };

  const start2FASetup = async () => {
    setSetupLoading(true);
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetupData(res.data);
      setVerifyCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    }
    setSetupLoading(false);
  };

  const verify2FA = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code');
    setSetupLoading(true);
    try {
      await api.post('/auth/2fa/verify', { code: verifyCode });
      toast.success('Two-factor authentication enabled!');
      setSetupData(null);
      setVerifyCode('');
      // Update local user state
      setUser(prev => ({ ...prev, totp_enabled: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    }
    setSetupLoading(false);
  };

  const disable2FA = async () => {
    if (!disablePassword) return toast.error('Enter your password to disable 2FA');
    setSetupLoading(true);
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      toast.success('Two-factor authentication disabled');
      setDisablePassword('');
      setShowDisable(false);
      setUser(prev => ({ ...prev, totp_enabled: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable 2FA');
    }
    setSetupLoading(false);
  };

  const is2FAEnabled = user?.totp_enabled;

  return (
    <div className="space-y-6 max-w-2xl animate-fade-in" data-testid="profile-page">
      <div>
        <h1 className="text-[22px] font-medium tracking-tight">My Profile</h1>
      </div>

      {/* Account Info */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-bold flex items-center gap-2">
            <User className="h-4 w-4 text-primary" /> Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xl font-bold shrink-0">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <p className="font-medium">{user?.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-xs">
                  <Shield className="h-2.5 w-2.5 mr-0.5" /> {user?.role}
                </Badge>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">{user?.email}</span>
            </div>
            {user?.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">{user?.phone}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="border-border bg-card shadow-none" data-testid="2fa-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-bold flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-primary" /> Two-Factor Authentication
            {is2FAEnabled ? (
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs ml-auto">
                <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> Enabled
              </Badge>
            ) : (
              <Badge className="bg-muted text-muted-foreground border-border text-xs ml-auto">
                <XCircle className="h-2.5 w-2.5 mr-0.5" /> Disabled
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Add an extra layer of security to your account. When enabled, you&apos;ll need to enter a code from your authenticator app each time you log in.
          </p>

          {!is2FAEnabled && !setupData && (
            <Button
              onClick={start2FASetup}
              disabled={setupLoading}
              className="bg-primary text-primary-foreground"
              data-testid="2fa-enable-btn"
            >
              <Shield className="h-4 w-4 mr-1.5" /> {setupLoading ? 'Setting up...' : 'Enable 2FA'}
            </Button>
          )}

          {/* Setup flow - QR code + verification */}
          {setupData && (
            <div className="space-y-4 border border-primary/20 rounded-md p-4 bg-primary/5">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-primary">Step 1: Scan QR Code</p>
                <p className="text-xs text-muted-foreground">
                  Open your authenticator app (Google Authenticator, Microsoft Authenticator, or Authy) and scan this QR code:
                </p>
                <div className="flex justify-center py-2">
                  <img
                    src={setupData.qr_code}
                    alt="2FA QR Code"
                    className="w-48 h-48 rounded-md border border-border"
                    data-testid="2fa-qr-code"
                  />
                </div>
                <details className="text-xs">
                  <summary className="text-muted-foreground cursor-pointer hover:text-foreground">Can&apos;t scan? Enter key manually</summary>
                  <code className="block mt-1 p-2 bg-background rounded text-xs font-mono break-all select-all" data-testid="2fa-manual-key">
                    {setupData.secret}
                  </code>
                </details>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-semibold text-primary">Step 2: Verify Code</p>
                <p className="text-xs text-muted-foreground">
                  Enter the 6-digit code shown in your authenticator app to confirm setup:
                </p>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="bg-background border-border text-center text-xl tracking-[0.4em] font-mono max-w-xs"
                  data-testid="2fa-verify-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={verify2FA}
                    disabled={setupLoading || verifyCode.length !== 6}
                    className="bg-emerald-500 text-white hover:bg-emerald-600"
                    data-testid="2fa-verify-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" /> {setupLoading ? 'Verifying...' : 'Verify & Enable'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setSetupData(null); setVerifyCode(''); }}
                    data-testid="2fa-cancel-setup-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Disable flow */}
          {is2FAEnabled && !showDisable && (
            <Button
              variant="outline"
              className="text-destructive border-destructive/30"
              onClick={() => setShowDisable(true)}
              data-testid="2fa-disable-btn"
            >
              <XCircle className="h-4 w-4 mr-1.5" /> Disable 2FA
            </Button>
          )}

          {showDisable && (
            <div className="space-y-2 border border-destructive/20 rounded-md p-3 bg-destructive/5">
              <p className="text-xs text-destructive font-semibold">Confirm password to disable 2FA</p>
              <Input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
                className="bg-background border-border"
                data-testid="2fa-disable-password"
              />
              <div className="flex gap-2">
                <Button
                  onClick={disable2FA}
                  disabled={setupLoading || !disablePassword}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="2fa-disable-confirm-btn"
                >
                  {setupLoading ? 'Disabling...' : 'Disable 2FA'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowDisable(false); setDisablePassword(''); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-bold flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                className="bg-background border-border"
                placeholder="Enter current password"
                data-testid="current-password"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="bg-background border-border"
                placeholder="At least 6 characters"
                data-testid="new-password"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="bg-background border-border"
                placeholder="Re-enter new password"
                data-testid="confirm-password"
              />
            </div>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground" data-testid="change-password-btn">
              <Lock className="h-4 w-4 mr-1.5" /> {saving ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-bold flex items-center gap-2">
            {theme === 'dark' ? <Moon className="h-4 w-4 text-primary" /> : <Sun className="h-4 w-4 text-amber-400" />} Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</Label>
              <p className="text-xs text-muted-foreground">
                {theme === 'dark' ? 'Switch to light mode for a brighter look' : 'Switch to dark mode for a darker look'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Sun className={`h-3.5 w-3.5 ${theme === 'light' ? 'text-amber-400' : 'text-muted-foreground/40'}`} />
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={() => {
                  toggleTheme();
                  toast.success(theme === 'dark' ? 'Switched to Light Mode' : 'Switched to Dark Mode');
                }}
                data-testid="theme-toggle"
              />
              <Moon className={`h-3.5 w-3.5 ${theme === 'dark' ? 'text-primary' : 'text-muted-foreground/40'}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
